<template>
  <div class="w-100">
    <TreeSelect
      v-bind="$attrs"
      v-model="costCenterId"
      v-model:modal-visible="selectModalVisibility"
      :entity-name="t('global.board')"
      :multiple="selectionMode === 'multiple'"
      :elements="result?.SQDCBoards ?? []"
      :show-inline-create="false"
      :valid="valid"
      :loading="loading"
      :hide-path="true"
      :disabled="disabled"
      :disabled-nodes="combinedDisabledNodes"
      :clearable="clearable"
      :auto-select-first="autoSelectFirst"
      :inline="inline"
    >
      <template v-if="'prepend' in $slots" #prepend>
        <slot name="prepend"></slot>
      </template>
      <template v-if="'append' in $slots" #append> <slot name="append" /> </template
    ></TreeSelect>
  </div>
</template>

<script setup lang="ts">
import { useTranslation } from "@/language/translation";

import { computed, ref, type PropType } from "vue";
import { useSQDCBoardsForSelectQuery } from "@/graphql";
import TreeSelect from "@/components/shared/searchSelect/treeSelect/TreeSelect.vue";
import { useAccessRights } from "@/functions/useAccessRights";
import store from "@/store";
import { hasRight } from "@/functions/authorizationState";
import {
  getBoardsDisabledWithoutAccess,
  restrictBoardSelectVariablesToSiteIds,
  type SQDCBoardSelectVariables,
} from "./SQDCBoardSelectHelper";
const { t } = useTranslation();

const props = defineProps({
  variables: {
    type: Object as PropType<SQDCBoardSelectVariables>,
    required: false,
    default: () => ({}),
  },
  requiredRight: {
    type: String,
    required: false,
    default: undefined,
  },
  selectionMode: {
    type: String as PropType<"single" | "multiple">,
    required: false,
    default: "single",
  },
  autoSelectFirst: {
    type: Boolean,
    required: false,
    default: false,
  },
  valid: {
    type: Boolean,
    required: false,
    default: undefined,
  },
  clearable: {
    type: Boolean,
    required: false,
    default: true,
  },
  disabled: {
    type: Boolean,
    required: false,
    default: false,
  },
  disabledNodes: {
    type: Array as PropType<
      {
        key: string | number;
        includeChildren: boolean;
      }[]
    >,
    required: false,
    default: undefined,
  },
  disabledWithoutRight: {
    type: String,
    required: false,
    default: undefined,
  },
  inline: {
    type: Boolean,
    required: false,
    default: false,
  },
});

const accessRights = useAccessRights();
const queryVariables = computed(() => {
  if (props.requiredRight == null) {
    return props.variables;
  }
  return restrictBoardSelectVariablesToSiteIds(
    props.variables,
    accessRights.siteIdsWithRight(props.requiredRight),
  );
});

const { result, loading } = useSQDCBoardsForSelectQuery(
  queryVariables,
  computed(() => ({ enabled: !props.disabled })),
);

const combinedDisabledNodes = computed(() => [
  ...(props.disabledNodes ?? []),
  ...(props.disabledWithoutRight == null
    ? []
    : getBoardsDisabledWithoutAccess(
        result.value?.SQDCBoards ?? [],
        accessRights.siteIdsWithRight(props.disabledWithoutRight),
        Number(store.getters["login/userId"]),
        hasRight("systemSettings.admin"),
      )),
]);

const costCenterId = defineModel("modelValue", {
  type: [Array, Number] as PropType<number | number[] | undefined>,
  default: undefined,
});

const selectModalVisibility = ref(false);
</script>

<style lang="scss" scoped></style>
